import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './SignUp.css';
import AppBar from '../../components/AppBar';
import Footer from '../../components/Footer';
import { MdEmail, MdLock, MdPerson, MdVisibility, MdVisibilityOff } from 'react-icons/md';

// Social logos (put your real png paths)
import tiktokLogo from '../../assets/survey/tiktok.png';
import instagramLogo from '../../assets/survey/instagram.png';
import telegramLogo from '../../assets/survey/telegram.png';
import discordLogo from '../../assets/survey/discord.png';
import friendsLogo from '../../assets/survey/friends.png';
import otherLogo from '../../assets/survey/other.png';

import { AuthContext } from '../../context/AuthContext';
import { APP_CONFIG } from "../../config/appConfig";
import { Link } from 'react-router-dom';
import tracker from '../../utils/tracker';

// Same cookie reader Payment.js uses for the affiliate ref
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};



const SignUp = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [tempId, setTempId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const { login, user, loading: authLoading } = useContext(AuthContext);



  const navigate = useNavigate();

  const BASE_URL = APP_CONFIG.API_BASE_URL;

  const passwordsMatch = confirmPassword === '' || password === confirmPassword;
  const passwordLongEnough = password.length === 0 || password.length >= 8;

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/wallets', { replace: true });
    }
  }, [authLoading, user, navigate]);

  if (authLoading || user) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${BASE_URL}/auth/request-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();
      if (res.ok) {
        tracker.track('signup_started');
        setOtpSent(true);
        setTempId(data.tempId);
        if (data.message) {
          setInfoMessage(data.message);
        } else {
          setInfoMessage('');
        }
      } else {
        setError(data.error || 'Failed to request verification.');
        setInfoMessage('');
      }
    } catch (err) {
      console.error('Error requesting OTP:', err);
      setError('Network error. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${BASE_URL}/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // ensures cookie is sent and received
        body: JSON.stringify({
          tempId,
          otp,
          email,
          password,
          name: name.trim(),
          // referral link saved locally (30-day cookie) — recorded on the new
          // user doc as signupRef so admins can see who arrived via a ref link
          affiliateRef: getCookie('affiliate_ref') || null,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        // **Do NOT store JWT in localStorage**
        // Only store UI flag
        localStorage.setItem('isAuthenticated', 'true');

        // Update AuthContext with user info (token is not needed in JS)
        login(data.user, null);

        navigate('/purchase');
      } else {
        setError(data.error || 'Invalid or expired code.');
      }
    } catch (err) {
      console.error('OTP verification error:', err);
      setError('Verification failed. Try again.');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div>
      <div className="sign-up-page">
        <AppBar />
        <div className="sign-up-wrapper">
          <div className="sign-up-container">
            <h2 className="sign-up-heading">Sign Up</h2>
            <hr className="sign-up-divider" />
          
            {error && <p className="sign-up-error">{error}</p>}
            {infoMessage && <p className="sign-up-info">{infoMessage}</p>}

            <form onSubmit={otpSent ? handleOtpSubmit : handleSubmit} className="sign-up-form">
              {!otpSent && (
                <>
                  <label className="sign-up-label">Full Name</label>
                  <div className="input-with-icon">
                    <MdPerson className="input-icon" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Full name"
                      className="sign-up-input"
                      maxLength={100}
                      required
                    />
                  </div>
                </>
              )}

              <label className="sign-up-label">Email</label>
              <div className="input-with-icon">
                <MdEmail className="input-icon" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className="sign-up-input"
                  required={!otpSent}
                  disabled={otpSent}
                />
              </div>

              <label className="sign-up-label">Password</label>
              <div className="input-with-icon">
                <MdLock className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className={`sign-up-input sign-up-input-padded-right${!otpSent && !passwordLongEnough ? ' sign-up-input-error-border' : ''}`}
                  required={!otpSent}
                  disabled={otpSent}
                />
                {!otpSent && (
                  <button
                    type="button"
                    className="sign-up-eye-icon"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                  >
                    {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                  </button>
                )}
              </div>
              {!otpSent && !passwordLongEnough && (
                <p className="sign-up-field-error">Password must be at least 8 characters.</p>
              )}

              {!otpSent && (
                <>
                  <label className="sign-up-label">Confirm Password</label>
                  <div className="input-with-icon">
                    <MdLock className="input-icon" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm password"
                      className={`sign-up-input sign-up-input-padded-right${!passwordsMatch ? ' sign-up-input-error-border' : ''}`}
                      required
                    />
                    <button
                      type="button"
                      className="sign-up-eye-icon"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <MdVisibilityOff /> : <MdVisibility />}
                    </button>
                  </div>
                  {!passwordsMatch && (
                    <p className="sign-up-field-error">Passwords do not match.</p>
                  )}
                </>
              )}

              {otpSent && (
                <>
                  <label className="sign-up-label">Enter verification code</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="6-digit code"
                    className="sign-up-input"
                    required
                  />
                </>
              )}

              <button
                type="submit"
                className="sign-up-button"
                disabled={loading || (!otpSent && (!passwordsMatch || !passwordLongEnough || !name.trim()))}
              >
                {otpSent ? 'Verify' : 'Sign Up'}
              </button>
            </form>

            <p className="sign-up-login-link">
              Already have an account? <Link to="/login">Login here</Link>
            </p>
          </div>
        </div>
        <p className="sign-up-terms">
          By signing up, you agree to our <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>
        </p>
        {loading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        )}

      </div>
      <Footer />
    </div>
  );
};

export default SignUp;